import { Platform } from "react-native";
import { RequiredDocuments } from "../../store/preProposalStore";
import {
  DocumentTypeEnum,
  isUnicoDocumentType,
  RequiredDocumentTypeEnum
} from "@repo/core/enums/proposals";
import { ProposalDocumentDTO } from "@repo/core/services/entities/Proposals";

export interface DocumentAnalysisResult {
  hasUnicoDocuments: boolean;
  hasOtherDocuments: boolean;
  unicoDocumentTypes: string[];
  otherDocumentTypes: string[];
}

const normalizeDocumentType = (documentType?: string): string => {
  return String(documentType || "").trim();
};

/** Identity / biometry codes captured by Unico or manual list (0001, 0010, 0009, 0026) */
export const isUnicoIdentityDocumentCode = (documentType: string): boolean => {
  const t = normalizeDocumentType(documentType);
  return (
    t === RequiredDocumentTypeEnum.DOCUMENT_FRONT ||
    t === RequiredDocumentTypeEnum.DOCUMENT_BACK ||
    t === RequiredDocumentTypeEnum.BIOMETRY_FACE ||
    t === RequiredDocumentTypeEnum.BIOMETRY ||
    t === DocumentTypeEnum.BIOMETRY ||
    t === DocumentTypeEnum.BIOMETRY_ID
  );
};

export const mergeUnicoUploadedIdentityTypes = (
  previous: string[] | undefined,
  additions: string[] | undefined
): string[] => {
  const set = new Set([
    ...(previous ?? []).map((t) => normalizeDocumentType(t)),
    ...(additions ?? []).map((t) => normalizeDocumentType(t))
  ]);
  return Array.from(set);
};

export const pickUnicoIdentityTypesFromUploadedSet = (
  types: Iterable<string>
): string[] => {
  return [...types].filter((t) => isUnicoIdentityDocumentCode(t));
};

/**
 * True when the mandatory document list includes biometry (0026).
 * Unico webview/SDK runs only when this is true; otherwise document front/back use manual capture.
 */
export const mandatoryDocumentsIncludeBiometry = (
  documents: RequiredDocuments[] | null | undefined
): boolean => {
  if (!documents?.length) {
    return false;
  }
  return documents.some(
    (document) =>
      normalizeDocumentType(document.strTipoDocumento) ===
      RequiredDocumentTypeEnum.BIOMETRY
  );
};

const getEquivalentUploadedTypes = (documentType: string): string[] => {
  if (
    documentType === RequiredDocumentTypeEnum.BIOMETRY ||
    documentType === RequiredDocumentTypeEnum.BIOMETRY_FACE
  ) {
    return [
      RequiredDocumentTypeEnum.BIOMETRY_FACE,
      RequiredDocumentTypeEnum.BIOMETRY,
      DocumentTypeEnum.BIOMETRY,
      DocumentTypeEnum.BIOMETRY_ID
    ];
  }

  return [documentType];
};

export const getUploadedProposalDocumentTypes = (
  documents: ProposalDocumentDTO[] = []
): string[] => {
  return Array.from(
    new Set(
      documents.map((document) =>
        normalizeDocumentType(document.strTipoDocumento)
      )
    )
  );
};

export const mergeRequiredDocumentsWithUploadedStatus = (
  requiredDocuments: RequiredDocuments[],
  uploadedProposalDocumentTypes: string[] = []
): RequiredDocuments[] => {
  return requiredDocuments.map((document) => {
    const documentType = normalizeDocumentType(document.strTipoDocumento);
    const equivalentTypes = getEquivalentUploadedTypes(documentType);
    const isUploaded = equivalentTypes.some((type) =>
      uploadedProposalDocumentTypes.includes(type)
    );

    return {
      ...document,
      isUploaded
    };
  });
};

/**
 * Analyzes required documents and categorizes them into Unico vs other documents
 * @param documents - Array of required documents
 * @returns Analysis result with flags and categorized document types
 */
export const analyzeRequiredDocuments = (
  documents: RequiredDocuments[]
): DocumentAnalysisResult => {
  const unicoDocumentTypes: string[] = [];
  const otherDocumentTypes: string[] = [];

  documents.forEach((document) => {
    // Convert to string to handle enum values properly
    const documentType = String(document.strTipoDocumento).trim();
    if (isUnicoDocumentType(documentType)) {
      unicoDocumentTypes.push(documentType);
    } else {
      otherDocumentTypes.push(documentType);
    }
  });

  return {
    hasUnicoDocuments: unicoDocumentTypes.length > 0,
    hasOtherDocuments: otherDocumentTypes.length > 0,
    unicoDocumentTypes,
    otherDocumentTypes
  };
};

export type ManualFlowDocumentsOptions = {
  includePendingUnicoDocuments?: boolean;
  /** Types already sent via Unico SDK — excluded from manual list when still in manual fallback */
  unicoUploadedIdentityDocumentTypes?: string[];
  /** Types that must not be requested in manual capture (e.g. 0017 after failed auto-upload on reuse) */
  excludeDocumentTypesFromManual?: string[];
};

/**
 * Documents shown on SelectDocuments (pending + uploaded for replace).
 * - After Unico success path: only non-identity docs (proof of address, etc.).
 * - Manual fallback: identity docs appear except those already sent in the Unico step.
 */
export const getManualFlowDocuments = (
  documents: RequiredDocuments[],
  options?: ManualFlowDocumentsOptions
): RequiredDocuments[] => {
  const {
    includePendingUnicoDocuments = false,
    unicoUploadedIdentityDocumentTypes = [],
    excludeDocumentTypesFromManual = []
  } = options || {};

  const unicoIdentityUploadedSet = new Set(
    unicoUploadedIdentityDocumentTypes.map((t) => normalizeDocumentType(t))
  );
  const excludedManualSet = new Set(
    excludeDocumentTypesFromManual.map((t) => normalizeDocumentType(t))
  );

  return documents.filter((document) => {
    const documentType = normalizeDocumentType(document.strTipoDocumento);
    if (excludedManualSet.has(documentType)) {
      return false;
    }
    const isIdentity = isUnicoIdentityDocumentCode(documentType);

    if (!includePendingUnicoDocuments) {
      return !isIdentity;
    }

    if (isIdentity && unicoIdentityUploadedSet.has(documentType)) {
      return false;
    }

    return true;
  });
};

/**
 * Pending rows only (subset of {@link getManualFlowDocuments}).
 */
export const getPendingManualDocuments = (
  documents: RequiredDocuments[],
  options?: ManualFlowDocumentsOptions
): RequiredDocuments[] => {
  const visible = getManualFlowDocuments(documents, options);
  return visible.filter((document) => document.isUploaded !== true);
};

export const WEB_BIOMETRY_STORAGE = {
  generateVerificationId: () => {
    return Platform.OS === "web" ? crypto.randomUUID() : "native";
  },
  save: (verificationId: string, params: unknown) => {
    if (Platform.OS !== "web") return;
    localStorage.setItem(verificationId, JSON.stringify(params));
  },
  get: (verificationId: string) => {
    if (Platform.OS !== "web") return null;
    return JSON.parse(localStorage.getItem(verificationId) || "null");
  },
  remove: (verificationId: string) => {
    if (Platform.OS !== "web") return;
    localStorage.removeItem(verificationId);
  },
  getNumber: (key: string): number | null => {
    if (Platform.OS !== "web") return null;

    const rawValue = localStorage.getItem(key);
    if (rawValue == null) {
      return null;
    }

    const parsed = Number(rawValue);
    return Number.isFinite(parsed) ? parsed : null;
  },
  setNumber: (key: string, value: number) => {
    if (Platform.OS !== "web") return;
    localStorage.setItem(key, String(value));
  },
  removeNumber: (key: string) => {
    if (Platform.OS !== "web") return;
    localStorage.removeItem(key);
  },
  clearProposalArtifacts: () => {
    if (Platform.OS !== "web") return;

    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);
      if (!key) continue;

      if (key.startsWith("unico-empty-capture-attempts:")) {
        keysToRemove.push(key);
        continue;
      }

      const rawValue = localStorage.getItem(key);
      if (!rawValue) continue;

      try {
        const parsedValue = JSON.parse(rawValue) as {
          processId?: unknown;
          params?: { callbackUri?: unknown };
        };
        const callbackUri = parsedValue?.params?.callbackUri;
        const isUnicoCallbackEntry =
          typeof parsedValue?.processId === "string" &&
          typeof callbackUri === "string" &&
          callbackUri.includes(`verificationId=${key}`);

        if (isUnicoCallbackEntry) {
          keysToRemove.push(key);
        }
      } catch {
        // Ignore storage entries that do not belong to the Unico flow.
      }
    }

    keysToRemove.forEach((key) => localStorage.removeItem(key));
  }
};
